import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { nanoid } from 'nanoid';
import { useLocalization } from '../../providers/LocalizationProvider';
import getFieldFromSchemaByName from '../../helpers/getFieldFromSchemaByName';
import dynamicDatePickerValueFormatter from '../../helpers/dynamicDatePickerValueFormatter';
import { useStore } from '../../providers/MobxProvider';
import LoadingEvent from '../UI/Skeleton/LoadingEvent';

const FormSearchResultAdvanced = forwardRef(
  (
    {
      schema,
      valueFormatter = undefined,
      preProcessSearchValues = args => args,
      resultsFound,
      storeName,
      onSearchClose = null,
    },
    ref,
  ) => {
    const { formatMessage } = useLocalization();
    const store = useStore();
    const [processedParams, setProcessedParams] = useState(null);
    const [loading, setLoading] = useState(true);

    if (!storeName) {
      throw Error('Store Name Not provided');
    }

    const { filters = [] } = store.domain.list.get(storeName);

    const onClose = () => {
      store.domain.list.reset(storeName);
      if (onSearchClose) {
        onSearchClose();
      }
    };

    const displayHelper = ({ value }) => {
      if (typeof value === 'string') {
        return value;
      }
      if (value?.operator) {
        return dynamicDatePickerValueFormatter(value);
      }
      if (value?.value && value?.label) {
        return value?.label;
      }
      return value;
    };

    const initialize = async () => {
      setLoading(true);
      let params = await preProcessSearchValues(filters);

      if (!Array.isArray(params) || params.length === 0) {
        params = null;
      }
      setProcessedParams(params);
      setLoading(false);
    };

    useEffect(() => {
      initialize();
    }, [JSON.stringify(filters)]);

    if (loading) {
      return (
        <LoadingEvent>
          <FormattedMessage id="LOADING" defaultMessage="Loading" />
          ...
        </LoadingEvent>
      );
    }

    if (!processedParams) {
      return null;
    }

    return (
      <Box ref={ref} sx={{ mb: 1 }}>
        <Alert
          variant="outlined"
          severity="info"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => onClose()}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }>
          <AlertTitle>
            {resultsFound && resultsFound > 0 ? (
              <FormattedMessage
                id="YOUR_SEARCH_RETURNED_COUNT_RESULTS"
                defaultMessage="Your search returned {count} results."
                values={{ count: resultsFound }}
              />
            ) : (
              <FormattedMessage
                id="YOUR_SEARCH_DID_NOT_RETURN_ANY_RESULTS"
                defaultMessage="Your search did not return any results."
              />
            )}
          </AlertTitle>
          <Box mt={2}>
            {processedParams.map(processedParam => {
              const fieldFound = getFieldFromSchemaByName(
                schema,
                processedParam?.field,
              );

              const displayValue = valueFormatter
                ? valueFormatter({
                    name: fieldFound?.name,
                    value: processedParam?.value,
                  }) ||
                  displayHelper({
                    name: fieldFound?.name,
                    value: processedParam?.value,
                  })
                : displayHelper({
                    name: fieldFound?.name,
                    value: processedParam?.value,
                  });
              const label = (
                <Stack gap={0.5} direction="row" alignItems="center">
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    component="span">
                    {fieldFound
                      ? formatMessage({
                          id: fieldFound?.label?.localeId,
                          defaultMessage: fieldFound?.label?.defaultMessage,
                        })
                      : processedParam?.field}
                  </Typography>
                  <Typography variant="subtitle2" component="span">
                    :
                  </Typography>
                  <Typography variant="body2" component="span">
                    {typeof displayValue === 'object'
                      ? JSON.stringify(displayValue)
                      : displayValue}
                  </Typography>
                </Stack>
              );
              return (
                <Chip
                  variant="outlined"
                  key={processedParam?.field || nanoid()}
                  sx={{ mr: 1 }}
                  label={label}
                />
              );
            })}
          </Box>
        </Alert>
      </Box>
    );
  },
);

export default observer(FormSearchResultAdvanced);
